import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePosStore } from '../../stores/pos.store';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';

interface BasketProps {
  onCheckout: () => void;
}

export default function Basket({ onCheckout }: BasketProps) {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';

  const {
    basket,
    customer,
    discount,
    taxes,
    removeItem,
    adjustQty,
    clearCustomer,
    clearBasket,
    getSubtotal,
    getTaxTotal,
    getTotal,
  } = usePosStore();

  const [editingQty, setEditingQty] = useState<number | null>(null);
  const [editingVal, setEditingVal] = useState('');

  const subtotal = getSubtotal();
  const taxTotal = getTaxTotal();
  const total = getTotal();

  let discountDisplay = '';
  if (discount) {
    discountDisplay =
      discount.type === 'percentage'
        ? `${discount.val}%`
        : formatCurrency(discount.val, currSym);
  }

  function startEditQty(index: number, currentQty: number) {
    setEditingQty(index);
    setEditingVal(String(currentQty));
  }

  function commitEditQty(index: number) {
    const parsed = parseInt(editingVal, 10);
    if (!isNaN(parsed)) {
      adjustQty(index, parsed);
    }
    setEditingQty(null);
  }

  return (
    <div className="basket">
      {/* Customer strip */}
      <div className="basket__customer">
        {customer ? (
          <div className="basket__customer-info">
            <span>
              {(customer.metadata ? JSON.parse(customer.metadata)?.name : null) ?? customer.id}
            </span>
            <button
              className="basket__customer-clear"
              onClick={clearCustomer}
              title="Remove customer"
            >
              ✕
            </button>
          </div>
        ) : (
          <span className="basket__customer-empty">No customer linked</span>
        )}
      </div>

      {/* Line items */}
      <div className="basket__items">
        {basket.length === 0 ? (
          <div className="basket__empty">Basket is empty</div>
        ) : (
          basket.map((b, index) => (
            <div key={index} className="basket__item">
              <div className="basket__item-name">{b.item.name ?? 'Unnamed'}</div>

              <div className="basket__item-controls">
                <button
                  className="basket__qty-btn"
                  onClick={() => adjustQty(index, b.qty - 1)}
                >
                  −
                </button>

                {editingQty === index ? (
                  <input
                    className="basket__qty-input"
                    type="number"
                    value={editingVal}
                    onChange={(e) => setEditingVal(e.target.value)}
                    onBlur={() => commitEditQty(index)}
                    onKeyDown={(e) => e.key === 'Enter' && commitEditQty(index)}
                    min={0}
                    autoFocus
                  />
                ) : (
                  <span
                    className="basket__qty"
                    onClick={() => startEditQty(index, b.qty)}
                    title="Click to edit"
                  >
                    {b.qty}
                  </span>
                )}

                <button
                  className="basket__qty-btn"
                  onClick={() => adjustQty(index, b.qty + 1)}
                >
                  +
                </button>
              </div>

              <div className="basket__item-price">
                {formatCurrency(
                  (b.item.pprice ?? b.item.price ?? 0) * b.qty,
                  currSym,
                )}
              </div>

              <button
                className="basket__item-remove"
                onClick={() => removeItem(index)}
                title="Remove item"
              >
                <i className="fa fa-trash-o" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Totals */}
      <div className="basket__totals">
        <div className="basket__total-row">
          <span>{t('subtotal', 'Subtotal')}</span>
          <span>{formatCurrency(subtotal, currSym)}</span>
        </div>

        {discount && (
          <div className="basket__total-row basket__total-row--discount">
            <span>{t('discount', 'Discount')} ({discountDisplay})</span>
            <span>
              −{formatCurrency(
                discount.type === 'percentage'
                  ? subtotal * (discount.val / 100)
                  : Math.min(discount.val, subtotal),
                currSym,
              )}
            </span>
          </div>
        )}

        {taxes.map((tax) => (
          <div key={tax.name} className="basket__total-row">
            <span>{tax.name} ({tax.value}%)</span>
            <span>{formatCurrency((subtotal - (discount ? (discount.type === 'percentage' ? subtotal * discount.val / 100 : Math.min(discount.val, subtotal)) : 0)) * (tax.value / 100), currSym)}</span>
          </div>
        ))}

        <div className="basket__total-row basket__total-row--total">
          <span>{t('total', 'Total')}</span>
          <span>{formatCurrency(total, currSym)}</span>
        </div>
      </div>

      {/* Actions — legacy style: big red Clear + big green Complete */}
      <div className="basket__actions">
        <button
          className="btn btn--danger"
          onClick={clearBasket}
          disabled={basket.length === 0}
        >
          <i className="fa fa-times" /> {t('clear', 'Clear')}
        </button>
        <button
          className="btn btn--success btn--lg"
          onClick={onCheckout}
          disabled={basket.length === 0}
        >
          <i className="fa fa-check" /> {formatCurrency(total, currSym)}
        </button>
      </div>
    </div>
  );
}
